import React, { useState, useEffect } from 'react';
// Inline SVG variants (attribution-free)
import Header from '../../components/Header';
import HeroSection from './HeroSection';
import IntroductionSection from './IntroductionSection';
import AppMenuSection from './AppMenuSection';
import LoglVideoSection from './LoglVideoSection';
import FooterSection from './FooterSection';
import PositionedMobileDemoForWeb from './PositionedMobileDemoForWeb';
import PositionedMobileDemo from './PositionedMobileDemo';
import { trackScrollDepth } from '../../utils/analytics';

const LandingPage = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [activeDemoScreen, setActiveDemoScreen] = useState('home');
  const [isDemoVisible, setIsDemoVisible] = useState(false);
  const [activeProgressSection, setActiveProgressSection] = useState(0);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  const [bottomOffset, setBottomOffset] = useState(0);
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const [lastY, setLastY] = useState(0);
  const [isMobileDemoVisible, setIsMobileDemoVisible] = useState(false);
  const [isInHeroSection, setIsInHeroSection] = useState(true);

  // Scroll depth tracking
  useEffect(() => {
    let tracked25 = false;
    let tracked50 = false;
    let tracked75 = false;
    let tracked100 = false;

    const trackScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (window.scrollY / scrollHeight) * 100;

      if (scrollPercent >= 25 && !tracked25) {
        trackScrollDepth(25);
        tracked25 = true;
      }
      if (scrollPercent >= 50 && !tracked50) {
        trackScrollDepth(50);
        tracked50 = true;
      }
      if (scrollPercent >= 75 && !tracked75) {
        trackScrollDepth(75);
        tracked75 = true;
      }
      if (scrollPercent >= 100 && !tracked100) {
        trackScrollDepth(100);
        tracked100 = true;
      }
    };

    window.addEventListener('scroll', trackScroll, { passive: true });
    return () => window.removeEventListener('scroll', trackScroll);
  }, []);

  // 스크롤 기반 섹션 감지
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const scrollCenter = scrollY + windowHeight / 2;

      // Get all sections
      const heroSection = document.querySelector('.hero-section');
      const introSection = document.querySelector('.introduction-section');
      const appMenuSection = document.getElementById('app-menu');
      const waitlistSection = document.querySelector('.waitlist-section');
      const joinUsSection = document.querySelector('.join-us-section');

      // Update progress indicator (9 sections: Hero, Intro, home, mingl, connect, talk, passport, Wait, Join)
      if (heroSection && scrollCenter < heroSection.offsetTop + heroSection.offsetHeight) {
        setActiveProgressSection(0); // Hero
      } else if (introSection && scrollCenter >= introSection.offsetTop && scrollCenter < introSection.offsetTop + introSection.offsetHeight) {
        setActiveProgressSection(1); // Intro
      } else if (appMenuSection) {
        const allMenuCards = appMenuSection.querySelectorAll('.menu-card');
        if (allMenuCards.length >= 5) {
          const menuCards = Array.from(allMenuCards).slice(0, 5);
          const appMenuTop = appMenuSection.offsetTop;

          let foundCard = false;
          menuCards.forEach((card, index) => {
            const cardTop = appMenuTop + card.offsetTop;
            const cardBottom = cardTop + card.offsetHeight;
            if (scrollCenter >= cardTop && scrollCenter < cardBottom) {
              setActiveProgressSection(2 + index); // 2=home, 3=mingl, 4=connect, 5=talk, 6=passport
              foundCard = true;
            }
          });

          if (!foundCard && scrollCenter >= appMenuTop && scrollCenter < appMenuTop + appMenuSection.offsetHeight) {
            setActiveProgressSection(6); // Default to passport if in app menu section
          }
        }
      }

      if (waitlistSection && scrollCenter >= waitlistSection.offsetTop && scrollCenter < waitlistSection.offsetTop + waitlistSection.offsetHeight) {
        setActiveProgressSection(7); // Wait
      } else if (joinUsSection && scrollCenter >= joinUsSection.offsetTop) {
        setActiveProgressSection(8); // Join
      }

      // Demo visibility and screen change (same as before)
      if (!appMenuSection) return;
      const allMenuCards = appMenuSection.querySelectorAll('.menu-card');
      if (allMenuCards.length === 0) return;

      const menuCards = Array.from(allMenuCards).slice(0, 5);
      const appMenuTop = appMenuSection.offsetTop;
      const firstCard = menuCards[0];
      const firstCardTop = appMenuTop + firstCard.offsetTop;
      const lastCard = menuCards[menuCards.length - 1];
      const lastCardTop = appMenuTop + lastCard.offsetTop;
      const lastCardBottom = lastCardTop + lastCard.offsetHeight;

      // currentSection 업데이트
      const sections = document.querySelectorAll('.section');
      sections.forEach((section, index) => {
        const sectionTop = section.offsetTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        if (scrollCenter >= sectionTop && scrollCenter < sectionBottom) {
          setCurrentSection(index);
        }
      });

      // 데모 표시 여부 및 화면 전환
      if (scrollCenter >= firstCardTop && scrollCenter < lastCardBottom) {
        setIsDemoVisible(true);
        const screens = ['home', 'mingl', 'connect', 'talk', 'passport'];

        menuCards.forEach((card, menuIndex) => {
          const cardTop = appMenuTop + card.offsetTop;
          const cardBottom = cardTop + card.offsetHeight;

          if (scrollCenter >= cardTop && scrollCenter < cardBottom) {
            setActiveDemoScreen(screens[menuIndex] || 'home');
          }
        });
      } else {
        setIsDemoVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 초기 실행
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Desktop detection and bottom floating banner positioning (stops above footer) - desktop only
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    const onScroll = () => {
      const isDesktopView = window.innerWidth >= 1024;
      if (!isDesktopView) {
        setBottomOffset(0);
        return;
      }

      const footer = document.querySelector('.footer-section');
      if (!footer) return;

      // 데스크톱: 기존 방식 유지 (절대 좌표 기반)
      const footerTop = footer.offsetTop;
      const viewportBottom = window.scrollY + window.innerHeight;
      const overlap = Math.max(0, viewportBottom - footerTop);
      setBottomOffset(overlap);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    onResize();
    onScroll();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Mobile Demo Section visibility detection
  useEffect(() => {
    const handleScroll = () => {
      const demoSection = document.getElementById('demo-section');
      if (!demoSection) {
        setIsMobileDemoVisible(false);
        return;
      }

      const rect = demoSection.getBoundingClientRect();
      // 섹션이 화면에 보이는지 확인 (상단이 화면 안에 있고, 하단이 화면 위에 있으면)
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      setIsMobileDemoVisible(isVisible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 실행
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 모바일에서 하단 배너 스크롤 숨김 효과 (Header와 동일한 로직)
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY || 0;
      // 모바일 프리뷰에서만 숨김 효과: 폭 768 미만에서만 적용
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const goingDown = currentY > lastY;
        // Mobile Demo가 보일 때는 항상 숨김 (역스크롤해도 숨김 유지)
        if (isMobileDemoVisible) {
          setHiddenByScroll(true);
        } else {
          // 내려가면 숨기고, 올리면 보이기
          setHiddenByScroll(goingDown && currentY > 10);
        }
      } else {
        setHiddenByScroll(false);
      }
      setLastY(currentY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastY, isMobileDemoVisible]);

  // Hero section 내부에 있는지 확인 (모바일/태블릿에서 날짜/텍스트 표시용)
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('.hero-section');
      if (!heroSection) return;

      const scrollY = window.scrollY || 0;
      const heroTop = heroSection.offsetTop;
      const heroHeight = heroSection.offsetHeight;
      const heroBottom = heroTop + heroHeight;

      // Hero section 내부에 있으면 true
      const inHero = scrollY >= heroTop && scrollY < heroBottom;
      setIsInHeroSection(inHero);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 실행
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('waitlist');
    waitlistSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDemo = () => {
    const demoSection = document.getElementById('app-menu');
    demoSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const shouldShowFloatingBanner = isDesktop || isInHeroSection;

  return (
    <div className="min-h-screen relative" style={{ scrollSnapType: 'y mandatory' }}>
      {/* Fixed Header */}
      <Header 
        onJoinWaitlist={scrollToWaitlist} 
        onTryDemo={scrollToDemo}
        isMobileDemoVisible={isMobileDemoVisible}
      />

      {/* Floating Demo Phone - Desktop Only */}
      <PositionedMobileDemoForWeb
        activeScreen={activeDemoScreen}
        currentSection={currentSection}
        isVisible={isDemoVisible}
      />


      {/* Hero Section - Single, Full Width */}
      <section
        className="section hero-section h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)' }}
      >
        <HeroSection
          onJoinWaitlist={scrollToWaitlist}
          onTryDemo={scrollToDemo}
          waitlistCount={1247}
        />
      </section>

      {/* Introduction Section - Single, Full Width */}
      <section
        id="features"
        className="section introduction-section min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)' }}
      >
        <IntroductionSection />
      </section>

      {/* App Menu Section - Split Left/Right for Local vs Global - Desktop Only */}
      <section id="app-menu" className="section app-menu-section relative hidden lg:block">
        {['home', 'mingl', 'connect', 'talk', 'passport'].map((menuKey, menuIndex) => {
          return (
            <div key={menuKey} className="menu-card" data-menu={menuKey}>
              <AppMenuSection menuKey={menuKey} menuIndex={menuIndex} />
            </div>
          );
        })}
      </section>

      {/* Mobile Demo Section - Mobile Only */}
      <div className="lg:hidden">
        <PositionedMobileDemo />
      </div>

      {/* Logl Video Section - Single, Full Width */}
      <section
        id="video"
        className="section video-section min-h-screen flex items-center justify-center sm:pb-24 md:pb-28 lg:pb-0"
        style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #eef0f3 100%)' }}
      >
        <LoglVideoSection />
      </section>

      {/* Floating bottom banner - Desktop always, Mobile/Tablet only on hero */}
      {/* 2026-06-18 [YJ] 비활성화: "2026.01. 한국에서 처음 시작합니다" 플로팅 배너 숨김 (원복하려면 false 제거) */}
      {false && shouldShowFloatingBanner && (
        <div className={`fixed left-0 right-0 z-[60] px-4 sm:px-6 py-1.5 bg-black/50 backdrop-blur-sm transition-all duration-300 transform ${
          hiddenByScroll ? 'translate-y-full' : 'translate-y-0'
        }`} style={{ bottom: bottomOffset }}>
          <div className="max-w-7xl mx-auto flex items-center justify-center text-white text-sm sm:text-lg gap-2 sm:gap-4">
            <div className="flex items-baseline gap-1 sm:gap-2">
              <strong className="tracking-wide">2026.01.</strong>
              <span className= "text-xs sm:text-base">한국에서 처음 시작합니다</span>
            </div>
            {/* Provided external SVG (Bootstrap Icons rocket-takeoff) after text */}
            <span className="inline-flex items-center justify-center w-[36px] h-[36px] sm:w-[48px] sm:h-[48px] rounded-md bg-white/10 text-[#C8102E]" aria-label="rocket-takeoff">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="sm:w-[28px] sm:h-[28px]" fill="currentColor" viewBox="0 0 16 16">
                <path d="M9.752 6.193c.599.6 1.73.437 2.528-.362s.96-1.932.362-2.531c-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532"/>
                <path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9 9 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a10 10 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093q.1.026.16.045c.184.06.279.13.351.295l.029.073a3.5 3.5 0 0 1 .157.721c.055.485.051 1.178-.159 2.065m-4.828 7.475.04-.04-.107 1.081a1.54 1.54 0 0 1-.44.913l-1.298 1.3.054-.38c.072-.506-.034-.993-.172-1.418a9 9 0 0 0-.164-.45c.738-.065 1.462-.38 2.087-1.006M5.205 5c-.625.626-.94 1.351-1.004 2.09a9 9 0 0 0-.45-.164c-.424-.138-.91-.244-1.416-.172l-.38.054 1.3-1.3c.245-.246.566-.401.91-.44l1.08-.107zm9.406-3.961c-.38-.034-.967-.027-1.746.163-1.558.38-3.917 1.496-6.937 4.521-.62.62-.799 1.34-.687 2.051.107.676.483 1.362 1.048 1.928.564.565 1.25.941 1.924 1.049.71.112 1.429-.067 2.048-.688 3.079-3.083 4.192-5.444 4.556-6.987.183-.771.18-1.345.138-1.713a3 3 0 0 0-.045-.283 3 3 0 0 0-.3-.041Z"/>
                <path d="M7.009 12.139a7.6 7.6 0 0 1-1.804-1.352A7.6 7.6 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/>
              </svg>
            </span>
          </div>
        </div>
      )}

      {/* Footer Section - Single, Full Width */}
      <section className="section footer-section" style={{ background: '#f4f5f7' }}>
        <FooterSection />
      </section>
    </div>
  );
};

export default LandingPage;